import { useEffect, useState, useMemo, useCallback } from 'react';
import { HashRouter as Router } from 'react-router-dom';
import { locale, loadMessages, formatMessage } from 'devextreme/localization';
import LoadPanel from 'devextreme-react/load-panel';
import { NavigationProvider } from './contexts/navigation';
import { useScreenSizeClass } from './utils/media-query';
import Content from './Content';
import UnauthenticatedContent from './UnauthenticatedContent';
import { useAppSelector, useAppDispatch } from './contexts/hooks';
import { useApiConfiguration } from './api/api.configuration';
import deMessages from 'devextreme/localization/messages/es.json';
import { System, Orders, Patient, Login } from './localization';
import 'devexpress-gantt/dist/dx-gantt.css';
import './dx-styles.scss';
import './themes/theme';
import { useThemeContext, ThemeContext } from './themes/theme';
import { FormPopup, InfoPatient, UpdateBanner } from './components';
import { setModalPatient, setModePatient } from './contexts/slices/orders';
import { useApiPatient } from './api/api.patient';
import { startSignalRConnection, stopSignalRConnection } from './utils/signalRService';
import { setApiUrls } from './contexts/slices/config';

// Diccionario en español de DevExtreme (textos propios de sus componentes: diálogos,
// grids, calendarios, validaciones). Debe cargarse aquí, a nivel de módulo y antes del
// primer render. Antes lo cargaba como efecto secundario el módulo de Agenda de citas, que
// se evaluaba al arrancar; al pasar las páginas a carga diferida ese módulo ya no se ejecuta
// al inicio y los componentes quedaban en inglés.
loadMessages(deMessages);
locale('es');

function App() {
  const [lang, setLang] = useState('es');
  const { getConfig } = useApiConfiguration();
  const dispatch = useAppDispatch();
  locale(lang);
  const cnfIn = useAppSelector((config) => config.configuration);

  // Remover el loader inicial cuando React esté listo
  useEffect(() => {
    // React arrancó: limpiar el flag de auto-recuperación del index.html para
    // que un fallo futuro vuelva a tener derecho a un reintento automático.
    try {
      sessionStorage.removeItem('synerlab-boot-retry');
    } catch {}
    const initialLoader = document.getElementById('initial-loader');
    if (initialLoader) {
      // Agregar una pequeña demora para transición suave
      setTimeout(() => {
        initialLoader.style.opacity = '0';
        initialLoader.style.transition = 'opacity 0.3s ease-out';
        setTimeout(() => {
          initialLoader.remove();
        }, 300);
      }, 200);
    }
  }, []);

  // Watchdog del gate de configuración: si "Cargando configuración..." dura
  // más de lo razonable, ofrecer salidas al usuario en vez de esperar infinito.
  const [slowConfigLoad, setSlowConfigLoad] = useState(false);
  useEffect(() => {
    if (!cnfIn.Loading) {
      setSlowConfigLoad(false);
      return;
    }
    const timer = setTimeout(() => setSlowConfigLoad(true), 20000);
    return () => clearTimeout(timer);
  }, [cnfIn.Loading]);

  // Cargar configuración de URLs al inicializar la app
  useEffect(() => {
    const loadApiUrls = async () => {
      try {
        const response = await fetch('/config.json');
        const config = await response.json();
        dispatch(setApiUrls({ apiUrl: config.apiUrl, signalRUrl: config.singnalRUrl }));
      } catch (error) {
        console.error('Error loading API configuration:', error);
      }
    };
    loadApiUrls();
  }, [dispatch]);

  useMemo(async () => {
    if (!cnfIn.IsAuthenticated) {
      await getConfig();
    }
  }, [cnfIn.IsAuthenticated]);

  //Cargar lenguajes
  useEffect(() => {
    loadMessages(Login);
    loadMessages(System);
    loadMessages(Orders);
    loadMessages(Patient);
  }, []);

  //Abrimos la conexión signalR
  useEffect(() => {
    startSignalRConnection(dispatch, cnfIn.signalRUrl ?? undefined);
    return () => {
      stopSignalRConnection(dispatch);
    };
  }, [dispatch, cnfIn.signalRUrl]);

  useEffect(() => {
    if (cnfIn.Data?.Config_General) {
      const cfg = cnfIn.Data.Config_General.find((x) => x.Cgn_Nombre === 'AppLanguage')?.Cgn_Resultado;
      const nuevoLang = cfg === '1' ? 'es-MX' : 'en-US';

      setLang(nuevoLang);
      if (lang === '1') {
        loadMessages(deMessages);
      }
    }
  }, [cnfIn]);

  if (cnfIn.Loading) {
    const loaderContainerStyle = {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      zIndex: 10000,
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    };

    const loaderContentStyle = {
      textAlign: 'center' as const,
      maxWidth: '300px',
      width: '90%',
    };

    const spinnerStyle = {
      width: '40px',
      height: '40px',
      border: '3px solid #e0e0e0',
      borderTop: '3px solid #4BAAF3',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 20px auto',
    };

    const textStyle = {
      color: '#666',
      margin: '0',
      fontSize: '14px',
      fontWeight: 400,
    };

    const rescueButtonStyle = {
      padding: '8px 16px',
      fontSize: '13px',
      borderRadius: '4px',
      border: '1px solid #4BAAF3',
      cursor: 'pointer',
    };

    // Restablecimiento completo: borra el estado persistido (posible origen
    // del bloqueo) y las cachés antes de recargar. Cierra la sesión local.
    const hardReset = async () => {
      try {
        localStorage.removeItem('persist:root');
      } catch {}
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch {}
      window.location.replace(`${window.location.pathname}?nocache=${Date.now()}${window.location.hash}`);
    };

    return (
      <>
        <style>
          {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
        </style>
        <div style={loaderContainerStyle}>
          <div style={loaderContentStyle}>
            <div style={spinnerStyle}></div>
            <p style={textStyle}>Cargando configuración...</p>
            {slowConfigLoad && (
              <div style={{ marginTop: '25px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                <p style={{ ...textStyle, color: '#999', fontSize: '13px' }}>Esto está tardando más de lo normal.</p>
                <button style={{ ...rescueButtonStyle, background: '#4BAAF3', color: '#fff' }} onClick={() => window.location.reload()}>
                  Reintentar
                </button>
                <button style={{ ...rescueButtonStyle, background: 'transparent', color: '#4BAAF3' }} onClick={hardReset}>
                  Restablecer aplicación
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  if (cnfIn.IsAuthenticated) {
    return <Content />;
  }

  return <UnauthenticatedContent />;
}

export default function Root() {
  const screenSizeClass = useScreenSizeClass();
  const themeContext = useThemeContext();
  const { ModalPatient, ModePatient } = useAppSelector((x) => x.globalOrders.CurrentOrder);
  const dispatch = useAppDispatch();
  const { LoadingPatient } = useAppSelector((x) => x.globalOrders.CurrentOrder);
  const { createUser, editUser } = useApiPatient();
  const [dataForm, setDataForm] = useState<object>({});
  const currentPatient = useAppSelector((x) => x.globalOrders.CurrentOrder.PatientData);
  const currentPatientQuotation = useAppSelector((x) => x.globalQuotation.PatientData);
  const currentPatientPreOrder = useAppSelector((x) => x.globalPreOrder.PatientData);
  const Prm_PacC = useAppSelector((x) => x.userInfo.Permissions?.Prm_PacC) ?? false;
  const Prm_PacU = useAppSelector((x) => x.userInfo.Permissions?.Prm_PacU) ?? false;
  const currentContext = useAppSelector((x) => x.globalOrders.CurrentOrder.ContextPatient);
  const DefDocument = useAppSelector((x) => x.globalOrders.CurrentOrder.NoFindedDocument);

  const updateField = (field: string, castMode: 'UPPER' | 'LOWER') => (value: string | number) => {
    const fieldSegments = field.split('.');
    let updatedDataForm = { ...dataForm };

    if (fieldSegments.length > 1) {
      let nestedField = updatedDataForm[fieldSegments[0]] ? { ...updatedDataForm[fieldSegments[0]] } : {};
      nestedField[fieldSegments[1]] = castMode === 'UPPER' ? value.toString().toUpperCase() : castMode === 'LOWER' ? value.toString().toLocaleLowerCase() : value;

      updatedDataForm = { ...updatedDataForm, [fieldSegments[0]]: nestedField };
    } else {
      updatedDataForm = { ...updatedDataForm, [field]: castMode === 'UPPER' ? value.toString().toUpperCase() : castMode === 'LOWER' ? value.toString().toLocaleLowerCase() : value };
    }

    setDataForm(updatedDataForm);
  };

  const setVisible = () => {
    dispatch(setModalPatient({ visible: false, context: currentContext }));
    dispatch(setModePatient('view'));
    setDataForm({});
  };

  const createPatient = useCallback(() => {
    if (ModePatient === 'new' && Prm_PacC) {
      const dataToSend = { ...dataForm, Pc_Identificacion: DefDocument };
      createUser(dataToSend, currentContext);
      return;
    }
    if (ModePatient === 'update' && Prm_PacU) {
      editUser(dataForm, currentContext);
    }
  }, [dataForm]);

  useEffect(() => {
    if (ModePatient === 'update') {
      switch (currentContext) {
        case 'orders':
          setDataForm(currentPatient);
          break;
        case 'quotations':
          setDataForm(currentPatientQuotation);
          break;
        case 'preorders':
          setDataForm(currentPatientPreOrder);
          break;
      }
    } else {
      setDataForm({});
    }
  }, [ModePatient]);

  return (
    <Router>
      <ThemeContext.Provider value={themeContext}>
        <NavigationProvider>
          <div className={`app ${screenSizeClass}`}>{themeContext.isLoaded ? <App /> : ''}</div>
        </NavigationProvider>
        <FormPopup onSave={createPatient} loading={LoadingPatient} allowSave={ModePatient === 'view'} title={ModePatient === 'view' ? formatMessage('PatientView') : ModePatient === 'new' ? formatMessage('PatientNew') : formatMessage('PatientEdit')} visible={ModalPatient} setVisible={setVisible}>
          {ModalPatient && <InfoPatient data={dataForm} setData={updateField} />}
        </FormPopup>
        <UpdateBanner />
      </ThemeContext.Provider>
    </Router>
  );
}
